import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminShell from "../AdminShell";
import { adminFetch } from "../adminApi";
import { useAdminToast } from "../AdminToast";
import "./admin-group-tours.css";

/**
 * RAdminGroupTours — /admin/group-tours (Group Tour Enquiries).
 *
 * Submissions from grouptours.umoonaopalmine.com.au. Modeled on
 * RAdminBookings, with expandable card rows instead of a modal (an enquiry
 * carries far more content: contact block, single-stay fields or N tour-date
 * rows, tours, signature image, editable admin notes).
 *
 *  - mount: GET /api/admin/group-tour-enquiries + .../stats
 *  - status change: PUT /api/admin/group-tour-enquiries/{id} { status }
 *  - notes save:    PUT /api/admin/group-tour-enquiries/{id} { admin_notes }
 *  - status workflow: new_enquiry → contacted → booked / cancelled
 */

const STATUS_META = {
  new_enquiry: ["adm-chip--amber", "New Enquiry"],
  contacted: ["adm-chip--blue", "Contacted"],
  booked: ["adm-chip--teal", "Booked"],
  cancelled: ["adm-chip--red", "Cancelled"],
};

const STATUS_OPTIONS = [
  ["new_enquiry", "New Enquiry"],
  ["contacted", "Contacted"],
  ["booked", "Booked"],
  ["cancelled", "Cancelled"],
];

const TYPE_META = {
  single_stay: ["adm-chip--grey", "Single Stay"],
  multiple_dates: ["adm-chip--purple", "Multiple Dates"],
};

const FILTERS = [["", "All"], ...STATUS_OPTIONS];

const enquiryId = (enquiry) => enquiry.id || enquiry._id;

const formatDateShort = (dateString) => {
  if (!dateString) return "—";
  const d = new Date(dateString);
  if (isNaN(d)) return dateString;
  return d.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatCreated = (dateString) => {
  if (!dateString) return "—";
  const d = new Date(dateString.endsWith("Z") ? dateString : `${dateString}Z`);
  if (isNaN(d)) return formatDateShort(dateString);
  return d.toLocaleString("en-AU", {
    timeZone: "Australia/Adelaide",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const totalPeople = (enquiry) => {
  if (enquiry.enquiry_type === "single_stay") {
    return (enquiry.guests || 0) + (enquiry.staff || 0) + (enquiry.crew || 0);
  }
  return (enquiry.tour_dates || []).reduce(
    (sum, row) => sum + (row.guests || 0) + (row.crew || 0),
    0
  );
};

const summaryLine = (enquiry) => {
  if (enquiry.enquiry_type === "single_stay") {
    return `Arrive ${formatDateShort(enquiry.arrival_date)} · ${totalPeople(
      enquiry
    )} pax`;
  }
  const dates = enquiry.tour_dates || [];
  return `${dates.length} tour date${dates.length !== 1 ? "s" : ""} · ${totalPeople(
    enquiry
  )} pax`;
};

const RAdminGroupTours = () => {
  const navigate = useNavigate();
  const toast = useAdminToast();

  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [statusBusy, setStatusBusy] = useState({});
  const [noteDrafts, setNoteDrafts] = useState({});
  const [noteBusy, setNoteBusy] = useState({});

  const loadEnquiries = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminFetch("/api/admin/group-tour-enquiries", {
        navigate,
      });
      if (res.ok) {
        const data = await res.json();
        setEnquiries(data.enquiries || []);
      } else {
        toast.error("Failed to fetch group tour enquiries");
      }
    } catch (error) {
      if (!error.isAuthError) {
        console.error("Error fetching group tour enquiries:", error);
        toast.error("Error fetching group tour enquiries");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, toast]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await adminFetch("/api/admin/group-tour-enquiries/stats", {
        navigate,
      });
      if (res.ok) setStats(await res.json());
    } catch (error) {
      if (!error.isAuthError) {
        console.error("Error fetching enquiry stats:", error);
      }
    }
  }, [navigate]);

  useEffect(() => {
    loadEnquiries();
    fetchStats();
  }, [loadEnquiries, fetchStats]);

  const handleStatusChange = async (enquiry, status) => {
    const id = enquiryId(enquiry);
    try {
      setStatusBusy((prev) => ({ ...prev, [id]: true }));
      const res = await adminFetch(`/api/admin/group-tour-enquiries/${id}`, {
        method: "PUT",
        navigate,
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        setEnquiries((prev) =>
          prev.map((e) => (enquiryId(e) === id ? { ...e, status } : e))
        );
        fetchStats();
        toast.success(`Status set to ${STATUS_META[status]?.[1] || status}`);
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      if (!error.isAuthError) {
        console.error("Error updating status:", error);
        toast.error("Error updating status");
      }
    } finally {
      setStatusBusy((prev) => ({ ...prev, [id]: false }));
    }
  };

  const noteDraftFor = (enquiry) => {
    const id = enquiryId(enquiry);
    return noteDrafts[id] !== undefined
      ? noteDrafts[id]
      : enquiry.admin_notes || "";
  };

  const noteDirty = (enquiry) => {
    const id = enquiryId(enquiry);
    return (
      noteDrafts[id] !== undefined &&
      noteDrafts[id] !== (enquiry.admin_notes || "")
    );
  };

  const saveNotes = async (enquiry) => {
    const id = enquiryId(enquiry);
    const admin_notes = noteDrafts[id] ?? "";
    try {
      setNoteBusy((prev) => ({ ...prev, [id]: true }));
      const res = await adminFetch(`/api/admin/group-tour-enquiries/${id}`, {
        method: "PUT",
        navigate,
        body: JSON.stringify({ admin_notes }),
      });
      if (res.ok) {
        setEnquiries((prev) =>
          prev.map((e) => (enquiryId(e) === id ? { ...e, admin_notes } : e))
        );
        setNoteDrafts((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        toast.success("Notes saved");
      } else {
        toast.error("Failed to save notes");
      }
    } catch (error) {
      if (!error.isAuthError) {
        console.error("Error saving notes:", error);
        toast.error("Error saving notes");
      }
    } finally {
      setNoteBusy((prev) => ({ ...prev, [id]: false }));
    }
  };

  const visible = filter
    ? enquiries.filter((e) => e.status === filter)
    : enquiries;

  return (
    <AdminShell
      active="group-tours"
      pageClass="pg-admin-group-tours"
      eyebrow="Tours"
      title="Group Tours"
      subtitle="Enquiries from grouptours.umoonaopalmine.com.au"
      actions={
        <span className="pg-admin-group-tours">
          <button
            type="button"
            className="adm-iconbtn"
            onClick={() => {
              loadEnquiries();
              fetchStats();
            }}
            title="Refresh"
            aria-label="Refresh"
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="#2a2018"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 11a8 8 0 10-2 6" />
              <path d="M20 4v5h-5" />
            </svg>
          </button>
        </span>
      }
    >
      <div className="pg-admin-group-tours">
        {/* ---- KPI row ---- */}
        <div className="adm-grid4 gt-kpis">
          <div className="adm-stat">
            <div className="adm-stat-top">
              <span className="adm-stat-label">Total Enquiries</span>
              <span className="adm-stat-icon adm-stat-icon--teal" />
            </div>
            <div className="adm-stat-value">
              {(stats.total || 0).toLocaleString("en-AU")}
            </div>
            <div className="adm-stat-sub adm-stat-sub--teal">All time</div>
          </div>
          <div className="adm-stat">
            <div className="adm-stat-top">
              <span className="adm-stat-label">New Enquiries</span>
              <span className="adm-stat-icon adm-stat-icon--ember" />
            </div>
            <div className="adm-stat-value">
              {(stats.new_enquiry || 0).toLocaleString("en-AU")}
            </div>
            <div className="adm-stat-sub adm-stat-sub--ember">
              Awaiting contact
            </div>
          </div>
          <div className="adm-stat">
            <div className="adm-stat-top">
              <span className="adm-stat-label">Booked</span>
              <span className="adm-stat-icon adm-stat-icon--blue" />
            </div>
            <div className="adm-stat-value">
              {(stats.booked || 0).toLocaleString("en-AU")}
            </div>
            <div className="adm-stat-sub adm-stat-sub--blue">Confirmed groups</div>
          </div>
          <div className="adm-stat">
            <div className="adm-stat-top">
              <span className="adm-stat-label">This Month</span>
              <span className="adm-stat-icon adm-stat-icon--purple" />
            </div>
            <div className="adm-stat-value">
              {(stats.this_month || 0).toLocaleString("en-AU")}
            </div>
            <div className="adm-stat-sub adm-stat-sub--purple">New submissions</div>
          </div>
        </div>

        {/* ---- status filter chips ---- */}
        <div className="gt-filters">
          {FILTERS.map(([value, label]) => (
            <button
              key={value || "all"}
              type="button"
              className={"gt-filter" + (filter === value ? " is-on" : "")}
              onClick={() => setFilter(value)}
            >
              {label}
              {value && (
                <span className="gt-filter-count">{stats[value] ?? ""}</span>
              )}
            </button>
          ))}
        </div>

        {/* ---- enquiry card rows ---- */}
        {loading ? (
          <div className="adm-loading">
            <div className="adm-spinner" />
            Loading enquiries…
          </div>
        ) : visible.length === 0 ? (
          <div className="adm-empty">
            <div className="adm-empty-icon">🚌</div>
            <div className="adm-empty-title">No enquiries found</div>
            <div className="adm-empty-sub">
              {filter
                ? "No enquiries with this status."
                : "Group tour enquiries submitted on the booking site will appear here."}
            </div>
          </div>
        ) : (
          <div className="gt-list">
            {visible.map((enquiry) => {
              const id = enquiryId(enquiry);
              const [chipClass, chipLabel] =
                STATUS_META[enquiry.status] || STATUS_META.new_enquiry;
              const [typeClass, typeLabel] =
                TYPE_META[enquiry.enquiry_type] || TYPE_META.single_stay;
              const expanded = expandedId === id;
              const contact = enquiry.contact || {};
              return (
                <div key={id} className={"gt-card" + (expanded ? " is-open" : "")}>
                  <div className="gt-row">
                    <div className="gt-info">
                      <div className="gt-refline">
                        <span className="gt-ref">{enquiry.reference}</span>
                        <span className={`adm-chip ${chipClass}`}>{chipLabel}</span>
                        <span className={`adm-chip ${typeClass}`}>{typeLabel}</span>
                      </div>
                      <div className="gt-cust">
                        <strong>{contact.company_name}</strong>
                        {contact.group_name ? ` (${contact.group_name})` : ""} •{" "}
                        {contact.contact_name} • {contact.email}
                      </div>
                      <div className="gt-meta">
                        {summaryLine(enquiry)} · Submitted{" "}
                        {formatCreated(enquiry.created_at)}
                        {enquiry.admin_notes ? " · 📝 has notes" : ""}
                      </div>
                    </div>
                    <div className="gt-actions">
                      <select
                        className="adm-select gt-status"
                        value={enquiry.status}
                        disabled={!!statusBusy[id]}
                        onChange={(e) => handleStatusChange(enquiry, e.target.value)}
                        aria-label="Enquiry status"
                      >
                        {STATUS_OPTIONS.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="adm-btn adm-btn--ghost gt-expand"
                        onClick={() => setExpandedId(expanded ? null : id)}
                      >
                        {expanded ? "Hide details ▴" : "View details ▾"}
                      </button>
                    </div>
                  </div>

                  {expanded && (
                    <div className="gt-detail">
                      {/* Contact */}
                      <div className="gt-sec">
                        <div className="gt-sec-title">Contact</div>
                        <div className="gt-grid">
                          <div>
                            <div className="gt-dlabel">Company / operator</div>
                            <div className="gt-dval">{contact.company_name || "—"}</div>
                          </div>
                          {contact.group_name && (
                            <div>
                              <div className="gt-dlabel">Group / school</div>
                              <div className="gt-dval">{contact.group_name}</div>
                            </div>
                          )}
                          <div>
                            <div className="gt-dlabel">Completed by</div>
                            <div className="gt-dval">{contact.contact_name || "—"}</div>
                          </div>
                          <div>
                            <div className="gt-dlabel">Email</div>
                            <div className="gt-dval">
                              <a className="adm-link" href={`mailto:${contact.email}`}>
                                {contact.email}
                              </a>
                            </div>
                          </div>
                          <div>
                            <div className="gt-dlabel">Phone</div>
                            <div className="gt-dval">{contact.phone || "—"}</div>
                          </div>
                          {(contact.driver_name || contact.driver_phone) && (
                            <div>
                              <div className="gt-dlabel">Driver</div>
                              <div className="gt-dval">
                                {[contact.driver_name, contact.driver_phone]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Stay / tour dates */}
                      {enquiry.enquiry_type === "single_stay" ? (
                        <div className="gt-sec">
                          <div className="gt-sec-title">Stay</div>
                          <div className="gt-grid">
                            <div>
                              <div className="gt-dlabel">Arrival</div>
                              <div className="gt-dval">
                                {formatDateShort(enquiry.arrival_date)}
                                {enquiry.arrival_time
                                  ? ` · ETA ${enquiry.arrival_time}`
                                  : ""}
                              </div>
                            </div>
                            <div>
                              <div className="gt-dlabel">Departure</div>
                              <div className="gt-dval">
                                {formatDateShort(enquiry.departure_date)}
                                {enquiry.nights != null
                                  ? ` · ${enquiry.nights} night(s)`
                                  : ""}
                              </div>
                            </div>
                            <div>
                              <div className="gt-dlabel">Numbers</div>
                              <div className="gt-dval">
                                {[
                                  enquiry.guests != null && `${enquiry.guests} guests`,
                                  enquiry.staff != null && `${enquiry.staff} staff`,
                                  enquiry.crew != null && `${enquiry.crew} crew (FOC)`,
                                ]
                                  .filter(Boolean)
                                  .join(" · ") || "—"}
                              </div>
                            </div>
                            <div>
                              <div className="gt-dlabel">Accommodation</div>
                              <div className="gt-dval">
                                {enquiry.accommodation || "Not specified"}
                              </div>
                            </div>
                          </div>
                          <div className="gt-grid" style={{ marginTop: 10 }}>
                            <div>
                              <div className="gt-dlabel">Tours requested</div>
                              <div className="gt-dval">
                                {(enquiry.tours || []).length
                                  ? enquiry.tours.join("  •  ")
                                  : "None selected"}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="gt-sec">
                          <div className="gt-sec-title">Tour Dates</div>
                          <div className="gt-dates">
                            {(enquiry.tour_dates || []).map((row, i) => (
                              <div className="gt-date-row" key={i}>
                                <span className="gt-date-num">{i + 1}</span>
                                <span className="gt-dval">
                                  {formatDateShort(row.tour_date)}
                                  {row.tour_time ? ` · ${row.tour_time}` : ""}
                                  {` · Exp ${row.experience}`}
                                  {` · ${row.guests} guests`}
                                  {row.crew ? ` · ${row.crew} crew (FOC)` : ""}
                                  {(row.church || []).length > 0
                                    ? ` · Church: ${row.church.join(", ")}`
                                    : ""}
                                  {row.driver ? ` · Driver: ${row.driver}` : ""}
                                  {row.comments ? ` — ${row.comments}` : ""}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Church + extra requirements */}
                      {((enquiry.church || []).length > 0 ||
                        enquiry.extra_requirements) && (
                        <div className="gt-sec">
                          <div className="gt-sec-title">Requirements</div>
                          <div className="gt-grid">
                            {(enquiry.church || []).length > 0 && (
                              <div>
                                <div className="gt-dlabel">Church visits</div>
                                <div className="gt-dval">
                                  {enquiry.church.join("  •  ")}
                                </div>
                              </div>
                            )}
                            {enquiry.extra_requirements && (
                              <div>
                                <div className="gt-dlabel">Extra requirements</div>
                                <div className="gt-dval">
                                  {enquiry.extra_requirements}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Agreement / signature */}
                      <div className="gt-sec">
                        <div className="gt-sec-title">Agreement</div>
                        <div className="gt-agree">
                          {enquiry.agreement?.signature_data_url ? (
                            <img
                              className="gt-sig"
                              src={enquiry.agreement.signature_data_url}
                              alt={`Signature for ${enquiry.reference}`}
                            />
                          ) : (
                            <div className="gt-dval">No signature stored</div>
                          )}
                          <div className="gt-agree-meta">
                            <div>
                              <div className="gt-dlabel">Date signed</div>
                              <div className="gt-dval">
                                {formatDateShort(enquiry.agreement?.date_signed)}
                              </div>
                            </div>
                            <div>
                              <div className="gt-dlabel">Terms version</div>
                              <div className="gt-dval">
                                {enquiry.agreement?.terms_version || "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Admin notes */}
                      <div className="gt-sec">
                        <div className="gt-sec-title">Admin Notes</div>
                        <textarea
                          className="adm-input gt-notes"
                          rows={3}
                          placeholder="Internal notes about this enquiry…"
                          value={noteDraftFor(enquiry)}
                          onChange={(e) =>
                            setNoteDrafts((prev) => ({
                              ...prev,
                              [id]: e.target.value,
                            }))
                          }
                        />
                        <div className="gt-notes-actions">
                          <button
                            type="button"
                            className="adm-btn"
                            disabled={!noteDirty(enquiry) || !!noteBusy[id]}
                            onClick={() => saveNotes(enquiry)}
                          >
                            {noteBusy[id] ? "Saving…" : "Save Notes"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminShell>
  );
};

export default RAdminGroupTours;
